import { COGNITO_CLIENT_ID, COGNITO_REGION, DEMO_PASSWORD, DEMO_USERNAME } from "./constants";

/**
 * Mints the Cognito ID token that API Gateway's `$connect` route verifies.
 *
 * Same shape as the iOS `CognitoAuth`: `USER_PASSWORD_AUTH` is a single
 * unsigned JSON POST — no SigV4, no SRP, no SDK. The user pool client allows
 * that flow specifically so this stays forty lines of fetch.
 */

const ENDPOINT = `https://cognito-idp.${COGNITO_REGION}.amazonaws.com/`;

/** Re-mint a minute early so a token can't expire between here and the handshake. */
const RENEW_MARGIN_MS = 60_000;

let idToken: string | null = null;
let expiresAt = 0;
let refreshToken: string | null = null;

interface AuthResult {
  AuthenticationResult?: {
    IdToken?: string;
    ExpiresIn?: number;
    RefreshToken?: string;
  };
  message?: string;
}

async function authenticate(flow: string, parameters: Record<string, string>): Promise<string> {
  const response = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-amz-json-1.1",
      "X-Amz-Target": "AWSCognitoIdentityProviderService.InitiateAuth",
    },
    body: JSON.stringify({
      AuthFlow: flow,
      ClientId: COGNITO_CLIENT_ID,
      AuthParameters: parameters,
    }),
  });

  const json = (await response.json()) as AuthResult;
  const result = json.AuthenticationResult;
  if (!response.ok || !result?.IdToken) {
    throw new Error(json.message ?? "Couldn't reach the sign-in service.");
  }

  idToken = result.IdToken;
  expiresAt = Date.now() + (result.ExpiresIn ?? 3600) * 1000;
  if (result.RefreshToken) refreshToken = result.RefreshToken;
  return idToken;
}

export async function validIdToken(): Promise<string> {
  if (idToken && Date.now() < expiresAt - RENEW_MARGIN_MS) return idToken;

  if (refreshToken) {
    try {
      return await authenticate("REFRESH_TOKEN_AUTH", { REFRESH_TOKEN: refreshToken });
    } catch {
      // Revoked or expired refresh token — fall through to a password auth.
      refreshToken = null;
    }
  }

  return authenticate("USER_PASSWORD_AUTH", {
    USERNAME: DEMO_USERNAME,
    PASSWORD: DEMO_PASSWORD,
  });
}

/** Drops cached tokens so the next call re-authenticates — used when the
 * socket handshake is refused, which usually means a stale token. */
export function invalidate(): void {
  idToken = null;
  expiresAt = 0;
}
